import { AUTH_PERSIST_ID } from 'constants/store';
import { actions as authActions, selectors as authSelectors } from 'modules/auth';

const PERSIST_KEY = `persist:${AUTH_PERSIST_ID}`;

/**
 * Helper to safely parse a JSON string value from the persisted redux-persist state.
 * redux-persist stores each field as a JSON-stringified value within the parent object.
 *
 * @param {Object} persistedState  The parsed localStorage object
 * @param {string} key             The key to read
 * @param {*}      defaultValue    Fallback if parsing fails
 * @return {*}
 */
const parsePersistField = (persistedState, key, defaultValue) => {
    try {
        return persistedState[key] != null ? JSON.parse(persistedState[key]) : defaultValue;
    } catch {
        return defaultValue;
    }
};

/**
 * Checks if the incoming persisted auth state represents a "diverged" session context
 * (impersonation or company switch) compared to a normal logged-in user.
 *
 * @param {Object} newAuthState  The parsed localStorage auth state from the storage event
 * @return {boolean}  true if the other tab is in impersonation or switched-company mode
 */
const isIncomingContextDiverged = newAuthState => {
    const isImpersonating = parsePersistField(newAuthState, 'isImpersonatingUser', false);
    const isSwitched = parsePersistField(newAuthState, 'isSwitchedCompany', false);
    return isImpersonating || isSwitched;
};

/**
 * Checks if THIS tab's Redux state represents a "diverged" session context.
 *
 * @param {Object} state  The current Redux state
 * @return {boolean}
 */
const isCurrentContextDiverged = state => {
    const isImpersonating = authSelectors.isImpersonatingUser(state);
    const isSwitched = Boolean(state.auth?.isSwitchedCompany);
    return isImpersonating || isSwitched;
};

/**
 * Redux middleware that synchronizes auth state across browser tabs.
 *
 * Handles:
 * 1. Logout propagation — if any tab logs out, all other tabs redirect to logout
 * 2. Token refresh sync — if a tab refreshes its token (same user context),
 *    all other same-context tabs get the new token
 * 3. Impersonation guard — tabs in impersonation mode are isolated
 * 4. Company switch guard — tabs in switched-company mode are isolated
 *
 * The browser `storage` event only fires on OTHER tabs (not the tab that wrote).
 *
 * @param {Object} store  The Redux store
 * @return {Function}  The middleware function
 */
const crossTabTokenMiddleware = store => {
    window.addEventListener('storage', event => {
        if (event.storageArea !== localStorage || event.key !== PERSIST_KEY) {
            return;
        }

        // LOGOUT PROPAGATION (always, no exceptions)

        // localStorage key was fully deleted (e.g. localStorage.clear())
        if (!event.newValue) {
            window.location.href = '/auth/logout';
            return;
        }

        try {
            const newAuthState = JSON.parse(event.newValue);
            const newToken = parsePersistField(newAuthState, 'token', {});

            // Token was emptied — another tab logged out
            if (!newToken.access_token) {
                window.location.href = '/auth/logout';
                return;
            }

            // TOKEN SYNC (only for same user context)

            // If the OTHER tab is in a diverged context (impersonating or switched),
            // don't pick up its token — it belongs to a different user/company
            if (isIncomingContextDiverged(newAuthState)) {
                return;
            }

            // If THIS tab is in a diverged context, don't overwrite its token
            // with the other tab's token
            const state = store.getState();
            if (isCurrentContextDiverged(state)) {
                return;
            }

            // Both tabs are in normal context — sync if token actually changed
            const currentAccessToken = authSelectors.getAccessToken(state);
            if (newToken.access_token !== currentAccessToken) {
                store.dispatch(authActions.fetchToken.success(newToken));
            }
        } catch (e) {
            // Silently handle JSON parse errors — don't break the app
        }
    });

    return next => action => next(action);
};

export default crossTabTokenMiddleware;
